import React, { useState } from 'react';
import { TextField, Button, Card, CardContent, Typography, Grid, Stack } from '@mui/material';
import { useReceiveCommunication } from '../../shared/hooks/useReceiveCommunication';
import { TimerComponent } from '../../shared/components/Timer';
import { MFAComponent } from '../../shared/components/MFA';
import {
  useRenewMonthlyPassMutation,
  useRenewWeeklyPassMutation,
  useUpdateVrmMutation,
  useUpdateWeeklyVrmMutation,
  useValidateMonthlyPassMfaMutation,
  useValidateMonthlyUserPhoneMutation,
} from '../../generated/graphql';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';
import { PassItem, buildPassList } from './helper';

const ManagePass: React.FC = () => {
  const history = useHistory();
  const [smsSent, setSmsSent] = useState(false);
  const [token, setToken] = useState('');
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [passes, setPasses] = useState<PassItem[]>([]);
  const [countdown, setCountdown] = useState<Date | null>(null);
  const [editingPassId, setEditingPassId] = useState<string | null>(null);
  const [newVrm, setNewVrm] = useState('');
  const {
    PhoneComponent,
    phone,
    setPhone,
  } = useReceiveCommunication();
  const [validateMonthlyUserPhone, { loading: validateMonthlyUserPhoneLoading, error: validateMonthlyUserPhoneError }] = useValidateMonthlyUserPhoneMutation();
  const [validateMonthlyPassMfa, { loading: validateMonthlyPassMfaLoading, error: validateMonthlyPassMfaError }] = useValidateMonthlyPassMfaMutation();
  const [renewMonthlyPass, { loading: renewMonthlyPassLoading, error: renewMonthlyPassError }] = useRenewMonthlyPassMutation();
  const [renewWeeklyPass, { loading: renewWeeklyPassLoading, error: renewWeeklyPassError }] = useRenewWeeklyPassMutation();
  const [updateVrm, { loading: updateVrmLoading, error: updateVrmError }] = useUpdateVrmMutation();
  const [updateWeeklyVrm, { loading: updateWeeklyVrmLoading, error: updateWeeklyVrmError }] = useUpdateWeeklyVrmMutation();

  const handleSendSMS = async () => {
    const { data } = await validateMonthlyUserPhone({
      variables: {
        phone: phone,
      },
    });
    if (data?.validateMonthlyUserPhone.hasPasses) {
      setCountdown(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes countdown
      setSmsSent(true);
      setError('');
    } else {
      setError('No passes found. Please try again.');
    }
    setToken('');
  };

  const handleVerifyToken = async () => {
    const { data } = await validateMonthlyPassMfa({
      variables: {
        phone: phone,
        token: token,
      },
    });
    const response = data?.validateMonthlyPassMfa;
    const monthly = response?.monthlyPasses ?? [];
    const weekly = response?.weeklyPasses ?? [];
    const combined = [
      ...monthly.map((pass) => ({ type: 'monthly' as const, pass })),
      ...weekly.map((pass) => ({ type: 'weekly' as const, pass })),
    ] as PassItem[];
    if (combined.length > 0) {
      setVerified(true);
      setError('');
      setCountdown(null);
      setPasses(combined);
    } else {
      setError('No passes found. Please try again.');
    }
  };

  const handleRenewPass = async (passId: string, passType: 'monthly' | 'weekly') => {
    try {
      if (passType === 'monthly') {
        const { data } = await renewMonthlyPass({
          variables: { monthlyPassPermitId: passId },
        });
        if (data?.renewMonthlyPass) {
          history.push(`/renew/${data.renewMonthlyPass.id}`);
        }
      } else {
        const { data } = await renewWeeklyPass({
          variables: { weeklyPassPermitId: passId },
        });
        if (data?.renewWeeklyPass) {
          history.push(`/renew/${data.renewWeeklyPass.id}`);
        }
      }
    } catch (err) {
      setError('Failed to renew pass. Please try again.');
    }
  };

  const handleUpdateVrm = async (passId: string, passType: 'monthly' | 'weekly') => {
    try {
      if (passType === 'monthly') {
        const { data } = await updateVrm({
          variables: { monthlyPassPermitId: passId, newVrm: newVrm },
        });
        if (data?.updateVrm) {
          setPasses((prev) =>
            prev.map((item) =>
              item.type === 'monthly' && item.pass.id === passId
                ? { ...item, pass: { ...item.pass, vrm: data.updateVrm.vrm } }
                : item
            )
          );
          setEditingPassId(null);
          setNewVrm('');
          setError('');
        }
      } else {
        const { data } = await updateWeeklyVrm({
          variables: { weeklyPassPermitId: passId, newVrm: newVrm },
        });
        if (data?.updateWeeklyVrm) {
          setPasses((prev) =>
            prev.map((item) =>
              item.type === 'weekly' && item.pass.id === passId
                ? { ...item, pass: { ...item.pass, vrm: data.updateWeeklyVrm.vrm } }
                : item
            )
          );
          setEditingPassId(null);
          setNewVrm('');
          setError('');
        }
      }
    } catch (err) {
      setError('Failed to update VRM. Please try again.');
    }
  };

  const startEditing = (passId: string, currentVrm: string) => {
    setEditingPassId(passId);
    setNewVrm(currentVrm);
  };

  const cancelEditing = () => {
    setEditingPassId(null);
    setNewVrm('');
  };

  const renewLoading = renewMonthlyPassLoading || renewWeeklyPassLoading;
  const updateVrmLoadingAny = updateVrmLoading || updateWeeklyVrmLoading;
  const passList = buildPassList(passes);

  return (
    <Stack width="100%" spacing={2}>
      <Typography variant="h5" gutterBottom>
        Manage passes
      </Typography>
      {!verified ? (
        <div>
          <PhoneComponent phone={phone} setPhone={setPhone} disabled={smsSent} />
          {!smsSent ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendSMS}
              disabled={!phone}
              fullWidth
            >
              Send SMS
            </Button>
          ) : (
            <Stack alignItems={'center'}>
              <MFAComponent mfa={token} setMfa={setToken} />
              {countdown && (
                <TimerComponent
                  countdown={countdown}
                  onComplete={() => {
                    console.log('Countdown completed');
                  }}
                />
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleVerifyToken}
                disabled={!token}
                fullWidth
              >
                Verify Token
              </Button>
            </Stack>
          )}
          {error && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {error}
            </Typography>
          )}
        </div>
      ) : (
        <div>
          <Grid container spacing={2}>
            {passList.map((entry, idx) => {
              if (entry.kind === 'header') {
                return (
                  <Grid item xs={12} key={`header-${entry.label}`}>
                    <Typography variant="h6">{entry.label}</Typography>
                  </Grid>
                );
              }
              const { pass, type } = entry.item;
              return (
                <Grid item xs={12} key={`${type}-${pass.id}-${idx}`}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack>
                          <Typography variant="overline" color="textSecondary">
                            {type === 'monthly' ? 'Monthly' : 'Weekly'}
                          </Typography>
                          <Typography variant="h6">{pass.vrm}</Typography>
                          {pass.site?.name && (
                            <Typography color="textSecondary" variant="body2">
                              {pass.site.name}
                            </Typography>
                          )}
                          <Typography color="textSecondary">
                            Expiry: {DateTime.fromSeconds(pass.untilTime).toLocaleString(DateTime.DATETIME_MED)}
                          </Typography>
                          {editingPassId === pass.id && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <TextField
                                size="small"
                                value={newVrm}
                                onChange={(e) => setNewVrm(e.target.value)}
                                placeholder="Enter new VRM"
                                sx={{ minWidth: 150 }}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleUpdateVrm(pass.id, type)}
                                disabled={updateVrmLoadingAny || !newVrm.trim()}
                                size="small"
                                sx={{ fontSize: '12px' }}
                              >
                                {updateVrmLoadingAny ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={cancelEditing}
                                size="small"
                                sx={{
                                  fontSize: '12px',
                                  color: 'primary.main',
                                  borderColor: 'primary.main',
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                        {DateTime.fromSeconds(pass.untilTime) <= DateTime.now() && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleRenewPass(pass.id, type)}
                              disabled={renewLoading}
                              sx={{
                                fontSize: '12px',
                                minWidth: 110,
                                height: 36,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {renewLoading ? 'Renewing...' : 'Renew Pass'}
                            </Button>
                          </Stack>
                        )}
                        {DateTime.fromSeconds(pass.untilTime) > DateTime.now() && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => startEditing(pass.id, pass.vrm)}
                              disabled={editingPassId !== null}
                              sx={{
                                fontSize: '12px',
                                minWidth: 110,
                                height: 36,
                                whiteSpace: 'nowrap',
                                color: 'primary.main',
                                borderColor: 'primary.main',
                              }}
                            >
                              Update
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </div>
      )}
    </Stack>
  );
};

export default ManagePass;
